import { useState } from "react";
import Icons from "../../../components/icons";
import { Link, useNavigate } from "react-router-dom";
import ResendEmailModal from "./ResendVerifyEmailModal";
import { Toast } from "../../../components/Toast/Toast";

const AuthForm = ({
  formInputs,
  onChange,
  formType = "signup",
  showPasswordRequirements = false,
  handleSubmit,
  error,
  loader,
  handleForgetPassword,
}) => {
  const [fieldType, setFieldType] = useState("password");
  const toggleFieldType = () => {
    setFieldType((prev) => (prev === "password" ? "text" : "password"));
  };
  const [isOpenModal, setIsModalOpen] = useState(false);
  const [toast, setToast] = useState({
    message: "",
    type: "error",
    isVisible: false,
    subMessage: "",
  });
  const [isCheck, setIsChecked] = useState(false);
  const navigate = useNavigate();
  const password = formInputs?.password ?? "";
  const shouldValidatePassword =
    formType === "signup" && showPasswordRequirements;
  const passwordRequirements = [
    {
      label: "At least 12 characters",
      isValid: password.length >= 12,
    },
    {
      label: "At least one uppercase letter (A-Z)",
      isValid: /[A-Z]/.test(password),
    },
    {
      label: "At least one lowercase letter (a-z)",
      isValid: /[a-z]/.test(password),
    },
    {
      label: "At least one number (0-9)",
      isValid: /\d/.test(password),
    },
    {
      label: "At least one special character (! @ # $ %)",
      isValid: /[^A-Za-z0-9\s]/.test(password),
    },
  ];
  const isPasswordValid =
    !shouldValidatePassword ||
    passwordRequirements.every((requirement) => requirement.isValid);

  const showToast = (message) => {
    setToast({
      isVisible: true,
      message,
      type: "error",
      subMessage: "",
    });
    setTimeout(() => {
      setToast((prev) => ({ ...prev, isVisible: false }));
    }, 3000);
  };

  const handleSubmitForm = () => {
    if (shouldValidatePassword && !isPasswordValid) {
      showToast("Password must meet all minimum requirements");
    } else if (formType === "signup" && !isCheck) {
      showToast("Please Accept terms of service and privacy policy");
    } else {
      handleSubmit();
    }
  };

  return (
    <div>
      {formType === "signup" && <input
        type="text"
        required
        placeholder="Full Name"
        value={formInputs.userName}
        onChange={onChange}
        name="userName"
        className="mb-2 w-full rounded-md border border-input bg-background px-4 py-3 text-foreground placeholder:text-muted-foreground outline-none focus-visible:ring-2 focus-visible:ring-ring"
      />}
      <input
        type="email"
        required
        placeholder="name@example.com"
        value={formInputs.email}
        onChange={onChange}
        name="email"
        className="w-full rounded-md border border-input bg-background px-4 py-3 text-foreground placeholder:text-muted-foreground outline-none focus-visible:ring-2 focus-visible:ring-ring"
      />
      <div className="relative mt-2.5">
        <input
          type={fieldType}
          placeholder="password"
          value={formInputs.password}
          onChange={onChange}
          name="password"
          className="w-full rounded-md border border-input bg-background px-4 py-3 text-foreground placeholder:text-muted-foreground outline-none focus-visible:ring-2 focus-visible:ring-ring"
        />
        {formInputs.password && (
          <span
            className="absolute top-1/2 -translate-y-1/2 right-4 cursor-pointer"
            onClick={toggleFieldType}
          >
            <Icons id={fieldType === "password" ? "eyeIcon" : "eyeCrossIcon"} />
          </span>
        )}
      </div>
      {shouldValidatePassword && formInputs.password && (
        <div className="mt-3 px-1 text-left">
          <p className="font-semibold text-sm text-foreground">
            Your password must contain:
          </p>
          <div className="mt-2 space-y-1.5">
            {passwordRequirements.map((requirement) => (
              <p
                key={requirement.label}
                className={`flex items-center gap-2 text-sm ${requirement.isValid ? "text-[#16A34A]" : "text-[#EF4444]"
                  }`}
              >
                <span
                  className="w-4 text-center font-semibold"
                  aria-hidden="true"
                >
                  {requirement.isValid ? "✓" : "×"}
                </span>
                <span>{requirement.label}</span>
              </p>
            ))}
          </div>
        </div>
      )}
      {formType !== "signup" && (
        <p className="px-2.5 pt-3 text-muted-foreground text-xs text-left">
          <Link to={""} onClick={handleForgetPassword && handleForgetPassword}>
            Forgot password?
          </Link>
        </p>
      )}
      {formType === "signup" && (
        <div className="mt-3.5 px-3 text-left text-sm text-foreground flex items-center cursor-pointer">
          <input
            type="checkbox"
            onChange={(e) => setIsChecked(e.target.checked)}
          />
          <span className="ml-3 flex items-center gap-1">
            Agree to our
            <a
              href="https://www.maybetech.com/terms"
              target="_blank"
              className="border-b !border-foreground"
              rel="noreferrer"
            >
              Terms of Service
            </a>
            and
            <a
              href="https://www.maybetech.com/privacy"
              target="_blank"
              className="border-b !border-foreground"
              rel="noreferrer"
            >
              Privacy Policy
            </a>
          </span>
        </div>
      )}
      {error && (
        <p
          className={`text-red-500 pt-2 ${error && (error?.includes("Email is not verified") || (error.toLowerCase().includes("user not found") && !window.location?.hostname.includes("mida"))) && "cursor-pointer"}`}
          onClick={() =>
            error && error?.includes("Email is not verified")
              ? setIsModalOpen(true)
              : error &&
              error.toLowerCase().includes("user not found") &&
              !window.location?.hostname.includes("mida") &&
              navigate("/auth/sign-up")
          }
        >
          {error.toLowerCase().includes("invalid email or password")
            ? "Email and password didn’t match. Forgot your password? Reset it."
            : error.toLowerCase().includes("user not found") &&
              window.location?.hostname.includes("mida")
              ? "There is no user with this email address registered"
              : error.toLowerCase().includes("user not found")
                ? (
                  <>
                    There is no user with this email address registered please{" "}
                    <Link
                      to="/auth/sign-up"
                      className="underline font-medium"
                      onClick={(e) => e.stopPropagation()}
                    >
                      sign up here.
                    </Link>
                  </>
                )
                : error?.includes("Email is not verified")
                  ? "Email is not verified. Re-send verification email"
                  : error}
        </p>
      )}
      <button
        className={`w-full mt-11 text-center py-4 rounded font-medium bg-foreground text-background border border-foreground transition-colors ${formInputs.email === "" || formInputs.password === ""
          ? "opacity-50 cursor-not-allowed"
          : "hover:bg-foreground/90"
          }`}
        disabled={formInputs.email === "" || formInputs.password === ""}
        onClick={() => handleSubmitForm()}
        type="submit"
      >
        {loader ? "wait..." : formType === "signup" ? "Sign Up" : "Sign In"}
      </button>
      <Toast
        duration={300}
        message={toast.message}
        subMessage={toast.subMessage ?? ""}
        type={toast.type}
        visible={toast.isVisible}
      />
      <ResendEmailModal
        responseback={(message) =>
          setToast({ isVisible: true, message, type: "success" })
        }
        isModalOpen={isOpenModal}
        justCloseIt={() => setIsModalOpen(false)}
      />
    </div>
  );
};

export default AuthForm;
